import { useEffect, useRef, useState } from "react";
import api from "@/utils/axios-utils";
import type {
  StudentMastery,
  StandardNode,
  LessonInfo,
  MasteryTimelinePoint,
  MasteryTimelineResponse,
} from "../types/standards";
import { MasteryLevel } from "../types/enums";
import {
  MASTERY_CATEGORIES,
  groupLessons,
  hasDirectMastery,
  masteryLevel,
} from "../service/mastery";
import {
  isDemoAccount,
  loadDemoStandardsTimeline,
} from "../service/demoAnalytics";
import type { StandardsFrameworkConfig } from "../constants/standardsFrameworkConfig";
import MasteryTimelineChart, {
  TIMELINE_CHART_VIEW_W,
  TIMELINE_CHART_VIEW_H,
} from "./MasteryTimelineChart";

type TimelineState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      points: MasteryTimelinePoint[];
      /** The cache's own value for this cell, as of this fetch. */
      currentMastery: number | null;
      lessons: LessonInfo[];
      /** The replay no longer reproduces the cached mastery this cell shows. */
      isStale: boolean;
    };

export type DrawerTarget = {
  student: StudentMastery;
  std: StandardNode;
  domainCode: string;
  clusterLetter: string;
  clusterShort: string;
};

interface StandardDetailDrawerProps {
  target: DrawerTarget | null;
  onClose: () => void;
  onOpenExitTicket: (req: { attemptId: number; studentName: string }) => void;
  disableEscape?: boolean;
  /** What the grid calls a leaf, so the drawer names it the same way. */
  terms: StandardsFrameworkConfig["terms"];
}

const StandardDetailDrawer = ({
  target,
  onClose,
  onOpenExitTicket,
  disableEscape = false,
  terms,
}: StandardDetailDrawerProps) => {
  const drawerRef = useRef<HTMLElement>(null);
  const open = !!target;
  const cell = target ? target.student.cells[target.std.id] : undefined;
  const started = hasDirectMastery(cell);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    drawerRef.current?.focus();
    return () => {
      if (previous?.isConnected) previous.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!target || disableEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key !== "Tab") return;
      const drawer = drawerRef.current;
      const stops = drawer?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
      );
      const first = stops?.[0],
        last = stops?.[stops.length - 1];
      if (!first || !last) {
        e.preventDefault();
        drawer?.focus();
        return;
      }
      if (
        e.shiftKey &&
        (document.activeElement === first || document.activeElement === drawer)
      ) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [target, onClose, disableEscape]);

  const [timeline, setTimeline] = useState<TimelineState>({ status: "idle" });
  const [expandedLessons, setExpandedLessons] = useState<Set<string>>(
    new Set(),
  );

  // A lesson's attempts stay collapsed until the teacher asks for them, and
  // switching to a different cell shouldn't carry over which lessons were
  // left expanded on the previous one.
  useEffect(() => {
    setExpandedLessons(new Set());
  }, [target?.student.user_id, target?.std.id]);

  // Recomputed on demand per student+standard, so a teacher clicking through
  // cells quickly must not see a stale series from the previously open cell —
  // both the `cancelled` flag and aborting the in-flight request guard that.
  useEffect(() => {
    if (!target || target.student.user_id == null || !started) {
      setTimeline({ status: "idle" });
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setTimeline({ status: "loading" });
    // Demo accounts read the trajectory from the same frozen capture the grid
    // beside this drawer came from (see StandardsViewContent). Both were
    // replayed from one pass over the captured attempts, so a series' last
    // point equals its cell — the live endpoint, by contrast, would describe
    // whoever holds the captured user_ids today. AbortController does not reach
    // the fixture loader, so `cancelled` alone guards a fast click-through.
    const load = async () => {
      try {
        const res = isDemoAccount("timeline")
          ? {
              data: (await loadDemoStandardsTimeline(
                target.student.user_id!,
                target.std.id,
              )) as MasteryTimelineResponse,
            }
          : await api.get<MasteryTimelineResponse>(
              "/analytics/standards/timeline",
              {
                params: {
                  user_id: target.student.user_id,
                  standard_code: target.std.id,
                },
                signal: controller.signal,
              },
            );
        if (cancelled) return;
        setTimeline({
          status: "ready",
          points: res.data.points,
          currentMastery: res.data.current_mastery,
          lessons: res.data.lessons,
          isStale: res.data.is_stale,
        });
      } catch (err) {
        if (cancelled) return;
        console.error("[standards] timeline fetch failed", err);
        setTimeline({ status: "error" });
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [target, started]);

  // The grid cell was fetched when the dashboard loaded; the timeline is
  // fetched on open. Across a nightly refresh those are two cache generations,
  // and reading the number from one while drawing the chart from the other put
  // an "Approaching" pill next to a chart ending at 85%. So once the timeline
  // has answered, its own figures drive everything here — it is the fresher of
  // the two, and it is what the chart below is drawn from.
  const isTimelineReady = timeline.status === "ready";
  const mastery =
    (isTimelineReady ? timeline.currentMastery : null) ?? cell?.mastery ?? 0;
  const lessons = (isTimelineReady ? timeline.lessons : cell?.lessons) ?? [];

  const level = started
    ? masteryLevel(mastery, true)
    : MasteryLevel.NOT_STARTED;
  const cat = MASTERY_CATEGORIES[level];

  const gotSum = lessons.reduce((s, l) => s + l.got, 0);
  const totSum = lessons.reduce((s, l) => s + l.total, 0);
  const lessonGroups = groupLessons(lessons);

  const toggleLesson = (groupKey: string) => {
    setExpandedLessons((prev) => {
      const next = new Set(prev);
      next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey);
      return next;
    });
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[100] bg-black/40 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal={open ? true : undefined}
        aria-hidden={!open}
        tabIndex={-1}
        className={`et-standard-detail fixed top-0 right-0 h-full w-[min(420px,92vw)] z-[101] bg-white shadow-2xl font-mulish flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-label="Standard detail"
      >
        {target && (
          <>
            <div className="et-standard-detail-heading">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="et-standard-detail-close"
              >
                ✕
              </button>
              <h2>{target.student.name}</h2>
              <div className="et-standard-detail-path">
                <span>{target.domainCode}</span>
                <span className="opacity-50">›</span>
                <span>{target.clusterLetter}</span>
                <span className="opacity-50">›</span>
                <span className="break-words">{target.clusterShort}</span>
              </div>
              <h3>{target.std.id}</h3>
              <p>{target.std.desc}</p>
            </div>

            <div className="et-standard-detail-body">
              <span
                className="inline-block text-[0.72rem] font-extrabold rounded-full px-[11px] py-1"
                style={{ backgroundColor: cat.pill, color: cat.pillInk }}
              >
                {cat.label}
              </span>
              <div className="mt-[14px] rounded-[14px] border border-[#EEEEEE] bg-white p-3">
                {!started ? (
                  <div className="text-[0.8rem] text-[#B4B4B4] py-6 text-center">
                    No direct evidence on this {terms.standard} yet.
                  </div>
                ) : timeline.status === "loading" ||
                  timeline.status === "idle" ? (
                  <div
                    className="w-full rounded-[10px] bg-[#F6F8F9] animate-pulse"
                    style={{
                      aspectRatio: `${TIMELINE_CHART_VIEW_W} / ${TIMELINE_CHART_VIEW_H}`,
                    }}
                    aria-label="Loading mastery history"
                  />
                ) : timeline.status === "error" ? (
                  <div className="text-[0.8rem] text-[#B4B4B4] py-6 text-center">
                    Couldn&apos;t load mastery history for this {terms.standard}
                    .
                  </div>
                ) : timeline.points.length === 0 ? (
                  <div className="text-[0.8rem] text-[#B4B4B4] py-6 text-center">
                    No mastery measurements yet.
                  </div>
                ) : timeline.points.length === 1 ? (
                  <div className="text-[0.84rem] text-[#252525b3] py-6 text-center">
                    Only one measurement so far:{" "}
                    <b className="text-[#252525]">
                      {Math.round(timeline.points[0].mastery * 100)}%
                    </b>{" "}
                    on{" "}
                    {new Date(
                      `${timeline.points[0].date}T00:00:00`,
                    ).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                    .
                  </div>
                ) : (
                  <>
                    {timeline.isStale && (
                      <div className="mb-2 rounded-[10px] bg-[#FFF7E8] border border-[#F2DFB8] px-3 py-2 text-[0.78rem] text-[#7A5B16] leading-[1.4]">
                        Some of this student&apos;s past work has changed since
                        mastery was last calculated, so this history is a
                        preview and doesn&apos;t yet add up to the mastery
                        above. It will line up after tonight&apos;s update.
                      </div>
                    )}
                    <MasteryTimelineChart points={timeline.points} />
                  </>
                )}
              </div>

              {!started ? (
                <div className="mt-[18px] rounded-[14px] bg-[#F6F8F9] border border-dashed border-[#E2E5E9] p-[14px] text-[0.84rem] text-[#252525b3] leading-[1.4]">
                  {target.student.name} has no direct exit-ticket evidence on
                  this {terms.standard} yet.
                </div>
              ) : lessons.length === 0 ? (
                <div className="mt-[18px] rounded-[14px] bg-[#F6F8F9] border border-dashed border-[#E2E5E9] p-[14px] text-[0.84rem] text-[#252525b3] leading-[1.4]">
                  No lesson details are available for this {terms.standard}.
                </div>
              ) : (
                <div className="mt-[18px]">
                  <h3 className="et-standard-evidence-heading">
                    Supporting lessons &amp; exit tickets
                  </h3>
                  <div className="text-[0.82rem] text-[#252525b3] mb-3 leading-[1.4]">
                    {totSum
                      ? `${gotSum} of ${totSum} exit-ticket problems correct across completed lessons.`
                      : "No exit ticket completed yet. Still approaching."}
                  </div>
                  <div className="flex flex-col gap-2">
                    {lessonGroups.map((lg) => {
                      const lvl =
                        lg.total > 0
                          ? masteryLevel(lg.got / lg.total, true)
                          : MasteryLevel.NOT_STARTED;
                      const expanded = expandedLessons.has(lg.key);
                      const hasMultipleAttempts = lg.attempts.length > 1;
                      return (
                        <div key={lg.key} className="et-standard-evidence-card">
                          <div>
                            <div className="min-w-0">
                              <div className="font-bold text-[#252525] text-[0.84rem] break-words">
                                {lg.lesson_title}
                              </div>
                              <div className="flex items-center gap-2 text-[0.76rem] font-semibold text-[#252525b3] leading-[1.35] mt-1.5">
                                <span
                                  className="inline-block w-2 h-2 rounded-full flex-none"
                                  style={{
                                    background: MASTERY_CATEGORIES[lvl].dot,
                                  }}
                                />
                                {lg.got}/{lg.total} exit ticket problems right
                                {hasMultipleAttempts
                                  ? ` across ${lg.attempts.length} attempts`
                                  : ""}
                              </div>
                            </div>
                            {hasMultipleAttempts ? (
                              <button
                                type="button"
                                onClick={() => toggleLesson(lg.key)}
                                className="et-standard-evidence-action"
                              >
                                {expanded ? "Hide attempts" : "View attempts"}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  onOpenExitTicket({
                                    attemptId: lg.attempts[0].response_id,
                                    studentName: target.student.name,
                                  })
                                }
                                className="et-standard-evidence-action"
                              >
                                View exit ticket results
                              </button>
                            )}
                          </div>
                          {hasMultipleAttempts && expanded && (
                            <div className="flex flex-col gap-1.5 mt-2.5 pt-2.5 border-t border-[#E2E5E9]">
                              {lg.attempts.map((a, ai) => {
                                const alvl =
                                  a.total > 0
                                    ? masteryLevel(a.got / a.total, true)
                                    : MasteryLevel.NOT_STARTED;
                                return (
                                  <div
                                    key={a.response_id}
                                    className="flex items-center justify-between gap-2.5 pl-[10px]"
                                  >
                                    <div className="flex items-center gap-2 text-[0.76rem] font-semibold text-[#252525b3]">
                                      <span
                                        className="inline-block w-1.5 h-1.5 rounded-full flex-none"
                                        style={{
                                          background:
                                            MASTERY_CATEGORIES[alvl].dot,
                                        }}
                                      />
                                      Attempt {ai + 1}: {a.got}/{a.total}{" "}
                                      correct
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        onOpenExitTicket({
                                          attemptId: a.response_id,
                                          studentName: target.student.name,
                                        })
                                      }
                                      className="flex-none text-[0.7rem] font-bold text-white bg-[#1C9C6D] hover:bg-[#007047] rounded-[8px] px-2.5 py-1.5"
                                    >
                                      View results
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default StandardDetailDrawer;
